import { gql } from 'graphql-tag';
import { get, pick, set } from 'lodash';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '~/store';

const initialState = () => ({
  id: null,
  controller: null,
  data: {
    analytic: {
      count: 0,
      results: []
    }
  },
  query: {
    where: {},
    sort: {
      createdAt: 'DESC'
    },
    offset: 0,
    limit: 10
  },
  table: {
    data: {
      count: 0,
      results: []
    },
    headers: [
      {
        text: 'Product ID',
        align: 'center',
        sortable: false,
        value: 'productId'
      },
      {
        text: 'Product Image',
        align: 'center',
        sortable: false,
        value: 'productImage'
      },
      {
        text: 'Product Name',
        align: 'center',
        sortable: false,
        value: 'productName'
      },
      {
        text: 'User Click',
        align: 'center',
        sortable: false,
        value: 'totalClick'
      }
    ],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true
    },
    filter: {
      search: '',
      start: null,
      end: null,
      dateFilter: null
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['createdAt'],
      sortDesc: [true],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  export: {
    filter: {
      startDate: null,
      endDate: null
    }
  },
  exposure: {
    data: {
      summary: {},
      chart: {
        startDate: undefined,
        endDate: undefined,
        details: []
      }
    }
  },
  distinctByDay: {
    data: []
  },
  loading: {
    fetch: false,
    fetchExposure: false,
    fetchDistinctByDay: false
  }
});

export const state = () => initialState();

export type PartnerMerchantAnalyticState = ReturnType<typeof state>;

export const getters: GetterTree<PartnerMerchantAnalyticState, RootState> = {
  getQuery: ({ query }) => query,
  getTable: ({ table }) => table,
  getExposure: ({ exposure }) => exposure,
  getDistinctByDay: ({ distinctByDay }) => distinctByDay,
  getLoading: ({ loading }) => loading
};

export const mutations: MutationTree<PartnerMerchantAnalyticState> = {
  changeId: (state, id) => (state.id = id),
  changeData: (state, data) => (state.table.data = data),
  changeQuery: (state, query) => Object.assign(state.query, query),
  changeTableItems: (state, items) => {
    Object.assign(state.table, { items });
  },
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        ...filter
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  changeExposure: (state, data) => (state.exposure.data = data),
  changeDistinctByDay: (state, data) => (state.distinctByDay.data = data),
  changeExportFilter: (state, filter) => {
    Object.assign(state.export, {
      filter: {
        ...filter
      }
    });
  },
  setLoading: (state, loading: PartnerMerchantAnalyticState['loading']) =>
    (state.loading = Object.assign({}, state.loading, loading)),
  setController: (state, controller) => (state.controller = controller),
  resetState: (state) => Object.assign(state, initialState())
};

export const actions: ActionTree<PartnerMerchantAnalyticState, RootState> = {
  /**
   * Fetch analytics data.
   */
  async findAll({ commit, state, dispatch, rootGetters }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findAll: true });

      const variables = {
        ...state.query,
        where: {
          ...state.query.where,
          merchantId: rootGetters['partner/merchant/getId']
        }
      };
      const { data } = await api.query({
        query: gql`
          query FindAllProductActionClick(
            $where: PurchaseProductAnalyticFilter
            $offset: Int
            $limit: Int
          ) {
            Purchase {
              Product {
                Analytic {
                  findAllActionClick(
                    where: $where
                    offset: $offset
                    limit: $limit
                  ) {
                    count
                    results {
                      productId
                      productName
                      productImage
                      totalClick
                    }
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const analytics = data?.Purchase?.Product?.Analytic?.findAllActionClick;
      commit('changeData', pick(analytics, ['count', 'results']));
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('changeData', { count: 0, results: [] });
    } finally {
      commit('setLoading', { findAll: false });
    }
  },

  async fetchExposure({ commit, state, dispatch, rootGetters }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { fetchExposure: true });

      const { startDate, endDate } = state.export.filter || {};
      const parseDate = (dateStr) => {
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? null : parsed;
      };
      const variables = {
        where: {
          ...(startDate && { startDate: parseDate(startDate) }),
          ...(endDate && { endDate: parseDate(endDate) }),
          merchantIds: [rootGetters['partner/merchant/getId']]
        }
      };
      const { data } = await api.query({
        query: gql`
          query findAllChartAndSummary($where: MerchantExposureFilter) {
            Merchant {
              MerchantExposure {
                findAllChartAndSummary(where: $where) {
                  summary {
                    totalActiveUser
                    totalExposedUser
                    totalContentGenerated
                    totalFollowUpQuestion
                  }
                  chart {
                    startDate
                    endDate
                    details {
                      description
                      totalActiveUser
                      totalExposedUser
                      totalContentGenerated
                      totalFollowUpQuestion
                    }
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const merchantExposure =
        data?.Merchant?.MerchantExposure?.findAllChartAndSummary;
      commit('changeExposure', pick(merchantExposure[0], ['summary', 'chart']));
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('changeExposure', {
        startDate: undefined,
        endDate: undefined,
        details: []
      });
    } finally {
      commit('setLoading', { fetchExposure: false });
    }
  },

  async fetchDistinctByDay({ commit, state, dispatch, rootGetters }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { fetchDistinctByDay: true });

      const { startDate, endDate } = state.export.filter || {};
      const parseDate = (dateStr) => {
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? null : parsed;
      };
      const variables = {
        where: {
          ...(startDate && { startDate: parseDate(startDate) }),
          ...(endDate && { endDate: parseDate(endDate) }),
          merchantIds: [rootGetters['partner/merchant/getId']]
        }
      };
      const { data } = await api.query({
        query: gql`
          query findAllDistinctByDay($where: MerchantExposureFilter) {
            Merchant {
              MerchantExposure {
                findAllDistinctByDay(where: $where) {
                  count
                  results {
                    date
                    totalActiveUser
                    totalExposedUser
                    totalContentGenerated
                    totalFollowUpQuestion
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const distinctByDay =
        data?.Merchant?.MerchantExposure?.findAllDistinctByDay;
      commit('changeDistinctByDay', get(distinctByDay, 'results', []));
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('changeDistinctByDay', []);
    } finally {
      commit('setLoading', { fetchDistinctByDay: false });
    }
  },

  /**
   * Creates a new AbortController object instance.
   */
  controllerInit({ commit, state }) {
    if (!state.controller) commit('setController', new AbortController());
  },

  /**
   * Controller abort for canceled request.
   */
  controllerAbort({ commit, state }) {
    if (state.controller) {
      state.controller.abort();
      commit('setController', null);
    }
  }
};
